import { Controller } from '@hotwired/stimulus'
import { useEditor } from '../mixins/useEditor'
import { useSanitizer } from '../mixins/useSanitizer'

export default class extends Controller {
  static targets = ['textarea', 'spinner', 'fallback']

  // Mounting pulls ~1 MB over a dozen dynamic imports (TinyMCE core, its theme,
  // icons, five plugins, sanitize-html). Bound the wait: a stalled request never
  // rejects on its own, and the spinner is an opaque overlay over the field.
  MOUNT_TIMEOUT = 20000

  async connect () {
    this.#torndown = false

    try {
      await this.#withTimeout(this.#mount(), this.MOUNT_TIMEOUT)
    } catch (error) {
      // Never leave the overlay up. It covers a textarea that works perfectly
      // well on its own, and `description_raw` is sanitized server-side by
      // JobDescriptionHtml.from_html either way — so plain text is a valid way
      // to post a vacancy, not a degraded one worth blocking. A failed dynamic
      // import is permanent (the browser caches the rejection), so there is
      // nothing to retry: tell the reader why the toolbar is missing instead.
      this.#showFallback()
    } finally {
      this.#hideSpinner()
    }
  }

  disconnect () {
    this.#torndown = true
    this.instanceEditor?.destroy()
    this.instanceEditor = null
  }

  #torndown = false

  async #mount () {
    await Promise.all([useEditor(this), useSanitizer(this)])

    const editor = await this.init(this.textareaTarget)
    // tinymce.init resolves with the editors it actually created — an empty
    // array when the target is already claimed, which is how a re-mount over a
    // restored Turbo snapshot fails.
    if (!editor) throw new Error('TinyMCE returned no editor for the description field')

    // Disconnected while we were waiting: the element is detached, so mounting
    // into it would leak an editor nothing can reach.
    if (this.#torndown) {
      editor.destroy()
      return
    }

    this.instanceEditor = editor
    editor.on('PastePreProcess', this.#handlePrePaste.bind(this))
    editor.on('GetContent', this.#handleGetContent.bind(this))
    // TinyMCE edits in an iframe and never writes the source textarea per
    // keystroke, so listeners on it (e.g. field-counter) go stale while typing.
    // Bridge editor edits back: sync the content into the textarea, then emit a
    // native input event. `input` covers typing/paste; `SetContent` covers
    // undo/redo/programmatic changes. Harmless when nothing listens.
    editor.on('input SetContent', this.#syncTextarea.bind(this))

    // The editor can still arrive after MOUNT_TIMEOUT gave up on it; if it does,
    // it is the better field and the warning no longer tells the truth.
    this.#hideFallback()
  }

  // Reject on a stall so connect's catch/finally can fall back, instead of
  // awaiting a promise that may never settle. The abandoned work stays pending,
  // which is harmless — #mount checks #torndown before it touches the DOM.
  #withTimeout (promise, ms) {
    return Promise.race([
      promise,
      new Promise((resolve, reject) => setTimeout(() => reject(new Error('editor mount timeout')), ms))
    ])
  }

  #handlePrePaste (args) {
    args.content = this.sanitizeHtml(args.content)
  }

  #handleGetContent (args) {
    if (args.save) {
      this.textareaTarget.value = this.sanitizeHtml(args.content)
    }
  }

  // Sync the editor's (sanitized) HTML back into the source textarea via save()
  // — which triggers #handleGetContent — so the value equals what the server
  // validates as description_raw, then notify listeners with a native input event.
  #syncTextarea () {
    this.instanceEditor.save()
    this.textareaTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  #hideSpinner () {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add('d-none')
  }

  #showFallback () {
    if (this.hasFallbackTarget) this.fallbackTarget.classList.remove('d-none')
  }

  #hideFallback () {
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add('d-none')
  }
}
